import { useMemo, useState } from "react"
import * as Validator from "yup"
import { localOrUtcTimeToLocalDate } from "@sembark-travel/datetime-utils"
import {
	Alert,
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
} from "@sembark-travel/ui-base"
import { Dialog, DialogBody } from "@sembark-travel/ui-dialog"
import {
	ImageCropInputField,
	Form,
	SubmissionError,
	useFormState,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { AmenitiesField } from "../Amenities"
import { useAuthUser } from "../Auth"
import {
	AddressFields,
	CheckinCheckoutFields,
	HotelDetailsFields,
	type THotelDetailsFormValues,
} from "./NewItem"
import { hotelsXHR, useHotel, type IHotel } from "./store"
import {
	hotelDetailsPayload,
	hotelDetailsSchemaShape,
	toStarsFormValue,
} from "./utils"
import { acceptedImageTypes } from "../shared/utils"

// Subscribed on its own, so the errors of every keystroke don't re-render the
// whole form
function DetailsTabErrorNote() {
	const { errors, submitFailed } = useFormState<THotelDetailsFormValues>({
		subscription: { errors: true, submitFailed: true },
	})
	if (!submitFailed || !detailsFieldNames.some((name) => errors?.[name])) {
		return null
	}
	return (
		<Box as="span" fontSize="sm" color="danger">
			Error in hotel details
		</Box>
	)
}

// Every field except `amenities` lives on the "Hotel details" tab
const detailsFieldNames: Array<keyof THotelDetailsFormValues> = [
	"name",
	"stars",
	"currency",
	"location",
	"trip_destinations",
	"brand_code",
	"image",
	"address",
	"checkin_at",
	"checkout_at",
	"maps_url",
]

export function UpdateHotelInDialog({
	hotel,
	open,
	onClose,
	onSuccess,
}: {
	hotel: { id: number }
	open: boolean
	onClose: () => void
	onSuccess: (hotel: IHotel) => void
}) {
	const {
		hotel: savedHotel,
		isFetching,
		error,
	} = useHotel(open ? hotel.id : null)
	return (
		<Dialog open={open} onClose={onClose} title="Edit Hotel" lg>
			<DialogBody paddingX="0" paddingY="0">
				{isFetching ? (
					<Spinner padding="4" alignCenter />
				) : savedHotel ? (
					<EditHotelForm hotel={savedHotel} onSuccess={onSuccess} />
				) : error ? (
					<Box padding="6">
						<Alert status="error">{error.message}</Alert>
					</Box>
				) : null}
			</DialogBody>
		</Dialog>
	)
}

function hotelToFormValues(hotel: IHotel): THotelDetailsFormValues {
	const { address } = hotel
	return {
		name: hotel.name,
		stars: toStarsFormValue(hotel.stars),
		brand_code: hotel.brand_code || "",
		currency: hotel.currency,
		location: hotel.location,
		trip_destinations: hotel.trip_destinations,
		image: undefined,
		address: {
			line_1: address.line_1 || "",
			line_2: address.line_2 || "",
			landmark: address.landmark || "",
			postcode: address.postcode || "",
			city: address.location.city,
			state: address.location.state,
			country: address.location.country,
		},
		checkin_at: localOrUtcTimeToLocalDate(
			hotel.checkin_at_local,
			hotel.checkin_at
		),
		checkout_at: localOrUtcTimeToLocalDate(
			hotel.checkout_at_local,
			hotel.checkout_at
		),
		amenities: (hotel.amenities || []).map(({ key, name }) => ({ key, name })),
		maps_url: hotel.maps_url || "",
	}
}

// Nested values use bracket keys (`address[line_1]`, `amenities[0][key]`, `amenities[0][name]`),
// booleans are sent as 1/0 and nulls as "" (the api reads empty strings as null).
function appendToFormData(data: FormData, key: string, value: unknown) {
	if (value === undefined) return
	if (value === null) {
		data.append(key, "")
	} else if (value instanceof Blob) {
		data.append(key, value)
	} else if (Array.isArray(value)) {
		value.forEach((item, index) =>
			appendToFormData(data, `${key}[${index}]`, item)
		)
	} else if (typeof value === "boolean") {
		data.append(key, value ? "1" : "0")
	} else if (typeof value === "object") {
		Object.entries(value).forEach(([childKey, childValue]) =>
			appendToFormData(data, key ? `${key}[${childKey}]` : childKey, childValue)
		)
	} else {
		data.append(key, String(value))
	}
}

/**
 * Request body as multipart form data, for the endpoints that take an image
 * along with the rest of the fields.
 */
function payloadToFormData(payload: object): FormData {
	const data = new FormData()
	appendToFormData(data, "", payload)
	return data
}

function EditHotelForm({
	hotel,
	onSuccess,
}: {
	hotel: IHotel
	onSuccess: (hotel: IHotel) => void
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const canAttachTripDestinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)
	const [selectedTab, setSelectedTab] = useState<"details" | "amenities">(
		"details"
	)
	// kept in state so a revalidation of the hotel doesn't reset the edits
	const [initialValues] = useState(() => hotelToFormValues(hotel))
	const initialImageUrl = hotel.latest_media?.url
	const validate = useMemo(
		() =>
			validateFormValues(
				Validator.object().shape(
					hotelDetailsSchemaShape({
						requireTripDestinations: canAttachTripDestinations,
						requireImage: !initialImageUrl,
					})
				)
			),
		[canAttachTripDestinations, initialImageUrl]
	)

	return (
		<Form<THotelDetailsFormValues>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(async (values) => {
				const updatedHotel = await hotelsXHR(xhr).updateHotel(
					hotel.id,
					payloadToFormData(hotelDetailsPayload(values))
				)
				showSnackbar("Hotel updated successfully")
				onSuccess(updatedHotel)
			})}
			subscription={{ submitting: true }}
		>
			{({ form, handleSubmit, submitting }) => {
				function hasDetailsErrors() {
					const { errors } = form.getState()
					return detailsFieldNames.some((name) => errors?.[name])
				}
				function goToAmenities() {
					if (hasDetailsErrors()) {
						// touch the nested fields too (e.g. `address.city`), so errors show up
						form
							.getRegisteredFields()
							.filter((name) => name !== "amenities")
							.forEach((name) =>
								form.blur(name as keyof THotelDetailsFormValues)
							)
						return
					}
					setSelectedTab("amenities")
				}
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Tabs layout="col">
								<TabsList>
									<TabItem
										active={selectedTab === "details"}
										onClick={() => setSelectedTab("details")}
									>
										Hotel details
									</TabItem>
									<TabItem
										active={selectedTab === "amenities"}
										onClick={goToAmenities}
									>
										Amenities
									</TabItem>
								</TabsList>
								<TabContent active={selectedTab === "details"} padding="6">
									<Inline gap="8" alignItems="start" collapseBelow="md">
										<Stack gap="8" flex="1" minWidth="0">
											<HotelDetailsFields
												withTripDestinations={canAttachTripDestinations}
												hotelId={hotel.id}
											/>
											<Divider marginY="0" />
											<AddressFields />
											<Divider marginY="0" />
											<CheckinCheckoutFields />
										</Stack>
										<ImageCropInputField
											preview
											crop={{ aspect: 3 / 2, minHeight: 100 }}
											name="image"
											label="Hotel Image"
											initialImageUrl={initialImageUrl}
											altText={hotel.name}
											accept={acceptedImageTypes.join(",")}
										/>
									</Inline>
								</TabContent>
								<TabContent active={selectedTab === "amenities"} padding="6">
									<AmenitiesField name="amenities" type="hotel" />
								</TabContent>
							</Tabs>
							<Box paddingX="6">
								<SubmissionError />
								<DetailsTabErrorNote />
							</Box>
							<Divider marginY="0" />
							<Inline gap="4" padding="4" paddingTop="0" justifyContent="end">
								{selectedTab === "details" ? (
									<Button
										type="button"
										level="primary"
										disabled={submitting}
										onClick={(e) => {
											e.preventDefault()
											goToAmenities()
										}}
									>
										Next
									</Button>
								) : (
									<Button type="submit" level="primary" disabled={submitting}>
										{submitting ? <Icons.Spinner /> : null}{" "}
										{submitting ? "Updating..." : "Update"}
									</Button>
								)}
							</Inline>
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}
